import { faPenClip } from '@fortawesome/pro-regular-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, ModalCloseButton, ModalFooter, ModalHeader, type ModalProps, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useCallback, useMemo, useState } from 'react';
import { StyledModalTitle } from 'src/common/components/standard-action-modal/standard-action-modal.styles';
import { useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { WriteUpsTable } from 'src/features/room/components/write-ups-table';
import { type AddWriteupFormInput, addWriteupFormValidation } from 'src/features/room/components/writeup-modals/writeup-modal.constants';
import { useAddWriteupMutation } from 'src/features/room/room.slice';
import { AddWriteup } from './add-writeup';
import { AddWriteupConfirmation } from './add-writeup-confirmation';
import { StyledBody, StyledBodyText, StyledIcon } from './writeup-modal.styles';
import type { Writeup } from './writeup-modal.types';
const columnTitles: string[] = ['Write-up Title', 'Added By'];
const iconComponent = <StyledIcon icon={faPenClip} />;
let showWriteupsModalBody = <StyledBody>
    <StyledBodyText>There are no write-ups submitted.</StyledBodyText>
    <StyledBodyText>Teach, share knowledge and help other users by adding your own write-up.</StyledBodyText>
  </StyledBody>;
export const ShowWriteups = ({
  onOpenChange,
  open,
  roomCode,
  writeupsArray = [],
  skipWriteupsList = false,
  ...otherModalProps
}: ModalProps & {
  roomCode: string;
  writeupsArray?: Writeup[];
  skipWriteupsList?: boolean;
}) => {
  const [showWriteupsList, setShowWriteupsList] = useState<boolean>(!skipWriteupsList);
  const [showAddWriteupForm, setShowAddWriteupForm] = useState<boolean>(skipWriteupsList);
  const [showAddWriteupConfirmation, setShowAddWriteupConfirmation] = useState<boolean>(false);
  const {
    register,
    reset,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<AddWriteupFormInput>({
    resolver: yupResolver(addWriteupFormValidation),
    mode: 'onSubmit'
  });
  const [addWriteupTrigger, {
    isLoading
  }] = useAddWriteupMutation();
  const handleAddWriteup = useCallback(() => {
    setShowWriteupsList(false);
    setShowAddWriteupForm(true);
    setShowAddWriteupConfirmation(false);
  }, []);
  const onCloseWriteupModal = useCallback(() => {
    onOpenChange?.(false);
    reset();
    setTimeout(() => {
      setShowWriteupsList(!skipWriteupsList);
      setShowAddWriteupForm(skipWriteupsList);
      setShowAddWriteupConfirmation(false);
    }, 500);
  }, [onOpenChange, reset, skipWriteupsList]);
  const onSubmit = useCallback(async (writeupInput: AddWriteupFormInput) => {
    const {
      title,
      link
    } = writeupInput;
    try {
      const result = await addWriteupTrigger({
        roomCode,
        title,
        link
      }).unwrap();
      if (result) {
        reset();
        setShowWriteupsList(false);
        setShowAddWriteupForm(false);
        setShowAddWriteupConfirmation(true);
      }
    } catch (error: unknown) {
      reset();
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message as unknown as string : 'Uh-oh. An error while adding your write-up. Please try again later with valid inputs.');
    }
  }, [addWriteupTrigger, reset, roomCode]);
  let dismissButtonTitle = showWriteupsList || showAddWriteupForm ? 'Cancel' : 'OK';
  if (showWriteupsList && writeupsArray.length) {
    dismissButtonTitle = 'Close';
    showWriteupsModalBody = <WriteUpsTable writeupData={writeupsArray} columnHeaders={columnTitles} isModal />;
  }
  const showIcon = !(showAddWriteupForm || showAddWriteupConfirmation);
  const modalTitleText = showWriteupsList ? 'Write-ups' : 'Add Write-up';
  const showActionButton = !showAddWriteupConfirmation;
  const actionButtonTitle = showWriteupsList ? 'Add Write-up' : 'Submit';
  let modalBody = showWriteupsModalBody;
  if (showAddWriteupForm) modalBody = <AddWriteup register={register} errors={errors} />;else if (showAddWriteupConfirmation) modalBody = <AddWriteupConfirmation />;
  const addWriteupFormContent = useMemo(() => <>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {modalBody}
          <ModalFooter isSmall>
            <ModalCloseButton handleCloseButtonAction={onCloseWriteupModal} text={dismissButtonTitle} />
            {showActionButton && <Button variant="solid" color="primary" type="submit" isLoading={isLoading} disabled={isLoading}>
                {actionButtonTitle}
              </Button>}
          </ModalFooter>
        </form>
      </>, [actionButtonTitle, dismissButtonTitle, handleSubmit, isLoading, modalBody, onCloseWriteupModal, onSubmit, showActionButton]);
  const modalContent = useMemo(() => <>
        {modalBody}
        <ModalFooter isSmall>
          <ModalCloseButton handleCloseButtonAction={onCloseWriteupModal} text={dismissButtonTitle} />
          {showActionButton && <Button variant="solid" color="primary" isLoading={isLoading} disabled={isLoading} onClick={handleAddWriteup}>
              {actionButtonTitle}
            </Button>}
        </ModalFooter>
      </>, [actionButtonTitle, dismissButtonTitle, handleAddWriteup, isLoading, modalBody, onCloseWriteupModal, showActionButton]);
  return <Modal open={open} onOpenChange={onCloseWriteupModal} {...otherModalProps} isSmall data-sentry-element="Modal" data-sentry-component="ShowWriteups" data-sentry-source-file="show-writeups.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="show-writeups.tsx">
        {showIcon && iconComponent}
        <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="show-writeups.tsx">{modalTitleText}</StyledModalTitle>
      </ModalHeader>
      {showAddWriteupForm ? addWriteupFormContent : modalContent}
    </Modal>;
};